import { Injectable, inject } from "@angular/core";
import { BrowserService } from "@core/services/browser.service";

export type WebAuthnBrowserErrorCode =
  | "unsupported"
  | "invalid-options"
  | "not-allowed"
  | "aborted"
  | "invalid-state"
  | "not-supported"
  | "security"
  | "operation-failed";

export type WebAuthnCapabilityReason =
  | "not-browser"
  | "insecure-context"
  | "webauthn-unavailable"
  | "credentials-api-unavailable";

export interface WebAuthnCapability {
  supported: boolean;
  reason: WebAuthnCapabilityReason | null;
}

type WebAuthnJsonPrimitive = boolean | number | string | null;
export type WebAuthnJsonValue =
  | WebAuthnJsonPrimitive
  | WebAuthnJsonValue[]
  | { [key: string]: WebAuthnJsonValue };

export interface WebAuthnRegistrationCredential {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment: string | null;
  clientExtensionResults: { [key: string]: WebAuthnJsonValue };
  response: {
    attestationObject: string;
    clientDataJSON: string;
    transports: string[];
  };
}

export interface WebAuthnAuthenticationCredential {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment: string | null;
  clientExtensionResults: { [key: string]: WebAuthnJsonValue };
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string | null;
  };
}

/**
 * A stable error boundary around browser WebAuthn failures.
 *
 * Callers can use `code` to decide whether to retry the security-key ceremony
 * or offer an independently enabled TOTP/recovery-code method.
 */
export class WebAuthnBrowserError extends Error {
  override readonly name = "WebAuthnBrowserError";

  constructor(
    readonly code: WebAuthnBrowserErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

type PublicKeyCredentialCreationOptionsWithHints =
  PublicKeyCredentialCreationOptions & {
    hints?: string[];
  };

type PublicKeyCredentialRequestOptionsWithHints =
  PublicKeyCredentialRequestOptions & {
    hints?: string[];
  };

type WindowWithWebAuthn = Window & {
  PublicKeyCredential?: typeof PublicKeyCredential;
};

type WebAuthnOperation = "registration" | "authentication";

const PUBLIC_KEY_CREDENTIAL_TYPE = "public-key";
const MAX_UINT32 = 4_294_967_295;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*={0,2}$/;
const AUTHENTICATOR_TRANSPORTS = new Set<AuthenticatorTransport>([
  "ble",
  "hybrid",
  "internal",
  "nfc",
  "usb",
]);
const USER_VERIFICATION_REQUIREMENTS = new Set<UserVerificationRequirement>([
  "discouraged",
  "preferred",
  "required",
]);
const RESIDENT_KEY_REQUIREMENTS = new Set<ResidentKeyRequirement>([
  "discouraged",
  "preferred",
  "required",
]);
const AUTHENTICATOR_ATTACHMENTS = new Set<AuthenticatorAttachment>([
  "cross-platform",
  "platform",
]);
const ATTESTATION_PREFERENCES = new Set<AttestationConveyancePreference>([
  "direct",
  "enterprise",
  "indirect",
  "none",
]);

/**
 * Converts webauthn-rs JSON options to browser WebAuthn inputs and converts
 * the browser's binary responses back to base64url JSON.
 *
 * This service is deliberately stateless: challenges and credential material
 * are never persisted in browser storage.
 */
@Injectable({
  providedIn: "root",
})
export class WebAuthnBrowserService {
  private readonly browser = inject(BrowserService);

  getCapability(): WebAuthnCapability {
    const browserWindow = this.browser.getWindow();

    if (!browserWindow) {
      return { supported: false, reason: "not-browser" };
    }

    if (!browserWindow.isSecureContext) {
      return { supported: false, reason: "insecure-context" };
    }

    if (
      typeof (browserWindow as WindowWithWebAuthn).PublicKeyCredential !==
      "function"
    ) {
      return { supported: false, reason: "webauthn-unavailable" };
    }

    const credentials = browserWindow.navigator.credentials;
    if (
      !credentials ||
      typeof credentials.create !== "function" ||
      typeof credentials.get !== "function"
    ) {
      return { supported: false, reason: "credentials-api-unavailable" };
    }

    return { supported: true, reason: null };
  }

  isSupported(): boolean {
    return this.getCapability().supported;
  }

  async register(
    publicKeyJson: unknown,
    signal?: AbortSignal,
  ): Promise<WebAuthnRegistrationCredential> {
    try {
      const browserWindow = this.requireSupportedWindow();
      const publicKey = this.parseCreationOptions(publicKeyJson, browserWindow);
      const credential = await browserWindow.navigator.credentials.create({
        publicKey,
        signal,
      });

      return this.serializeRegistrationCredential(credential, browserWindow);
    } catch (error: unknown) {
      throw this.mapError(error, "registration");
    }
  }

  async authenticate(
    publicKeyJson: unknown,
    signal?: AbortSignal,
  ): Promise<WebAuthnAuthenticationCredential> {
    try {
      const browserWindow = this.requireSupportedWindow();
      const publicKey = this.parseRequestOptions(publicKeyJson, browserWindow);
      const credential = await browserWindow.navigator.credentials.get({
        publicKey,
        signal,
      });

      return this.serializeAuthenticationCredential(credential, browserWindow);
    } catch (error: unknown) {
      throw this.mapError(error, "authentication");
    }
  }

  private requireSupportedWindow(): Window {
    const capability = this.getCapability();
    const browserWindow = this.browser.getWindow();

    if (!capability.supported || !browserWindow) {
      const message =
        capability.reason === "insecure-context"
          ? "Security keys require a secure connection. Open the secure site or use another verification method."
          : "Security keys are not supported in this browser. Use another verification method.";

      throw new WebAuthnBrowserError("unsupported", message);
    }

    return browserWindow;
  }

  private parseCreationOptions(
    value: unknown,
    browserWindow: Window,
  ): PublicKeyCredentialCreationOptionsWithHints {
    const options = this.requireRecord(value, "publicKey");
    const rp = this.requireRecord(options["rp"], "publicKey.rp");
    const user = this.requireRecord(options["user"], "publicKey.user");
    const params = this.requireArray(
      options["pubKeyCredParams"],
      "publicKey.pubKeyCredParams",
    );

    if (params.length === 0) {
      throw this.invalidOptions("publicKey.pubKeyCredParams must not be empty");
    }

    const parsed: PublicKeyCredentialCreationOptionsWithHints = {
      rp: {
        id: this.requireNonEmptyString(rp["id"], "publicKey.rp.id"),
        name: this.requireNonEmptyString(rp["name"], "publicKey.rp.name"),
      },
      user: {
        id: this.decodeBase64Url(
          this.requireNonEmptyString(user["id"], "publicKey.user.id"),
          "publicKey.user.id",
          browserWindow,
        ),
        name: this.requireNonEmptyString(user["name"], "publicKey.user.name"),
        displayName: this.requireNonEmptyString(
          user["displayName"],
          "publicKey.user.displayName",
        ),
      },
      challenge: this.decodeBase64Url(
        this.requireNonEmptyString(options["challenge"], "publicKey.challenge"),
        "publicKey.challenge",
        browserWindow,
      ),
      pubKeyCredParams: params.map((param, index) =>
        this.parseCredentialParameter(
          param,
          `publicKey.pubKeyCredParams[${index}]`,
        ),
      ),
    };

    const timeout = this.parseOptionalTimeout(
      options["timeout"],
      "publicKey.timeout",
    );
    if (timeout !== undefined) {
      parsed.timeout = timeout;
    }

    const excludeCredentials = this.parseOptionalCredentialDescriptors(
      options["excludeCredentials"],
      "publicKey.excludeCredentials",
      browserWindow,
    );
    if (excludeCredentials !== undefined) {
      parsed.excludeCredentials = excludeCredentials;
    }

    const authenticatorSelection = this.parseOptionalAuthenticatorSelection(
      options["authenticatorSelection"],
      "publicKey.authenticatorSelection",
    );
    if (authenticatorSelection !== undefined) {
      parsed.authenticatorSelection = authenticatorSelection;
    }

    const attestation = this.parseOptionalEnum(
      options["attestation"],
      "publicKey.attestation",
      ATTESTATION_PREFERENCES,
    );
    if (attestation !== undefined) {
      parsed.attestation = attestation;
    }

    const extensions = this.parseOptionalExtensions(
      options["extensions"],
      "publicKey.extensions",
    );
    if (extensions !== undefined) {
      parsed.extensions = extensions;
    }

    const hints = this.parseOptionalStringArray(
      options["hints"],
      "publicKey.hints",
    );
    if (hints !== undefined) {
      parsed.hints = hints;
    }

    return parsed;
  }

  private parseRequestOptions(
    value: unknown,
    browserWindow: Window,
  ): PublicKeyCredentialRequestOptionsWithHints {
    const options = this.requireRecord(value, "publicKey");
    const allowCredentials = this.requireArray(
      options["allowCredentials"],
      "publicKey.allowCredentials",
    );
    const userVerification = this.parseRequiredEnum(
      options["userVerification"],
      "publicKey.userVerification",
      USER_VERIFICATION_REQUIREMENTS,
    );

    const parsed: PublicKeyCredentialRequestOptionsWithHints = {
      challenge: this.decodeBase64Url(
        this.requireNonEmptyString(options["challenge"], "publicKey.challenge"),
        "publicKey.challenge",
        browserWindow,
      ),
      rpId: this.requireNonEmptyString(options["rpId"], "publicKey.rpId"),
      allowCredentials: allowCredentials.map((descriptor, index) =>
        this.parseCredentialDescriptor(
          descriptor,
          `publicKey.allowCredentials[${index}]`,
          browserWindow,
        ),
      ),
      userVerification,
    };

    const timeout = this.parseOptionalTimeout(
      options["timeout"],
      "publicKey.timeout",
    );
    if (timeout !== undefined) {
      parsed.timeout = timeout;
    }

    const extensions = this.parseOptionalExtensions(
      options["extensions"],
      "publicKey.extensions",
    );
    if (extensions !== undefined) {
      parsed.extensions = extensions;
    }

    const hints = this.parseOptionalStringArray(
      options["hints"],
      "publicKey.hints",
    );
    if (hints !== undefined) {
      parsed.hints = hints;
    }

    return parsed;
  }

  private parseCredentialParameter(
    value: unknown,
    path: string,
  ): PublicKeyCredentialParameters {
    const parameter = this.requireRecord(value, path);
    const type = this.requirePublicKeyType(parameter["type"], `${path}.type`);
    const algorithm = parameter["alg"];

    if (typeof algorithm !== "number" || !Number.isSafeInteger(algorithm)) {
      throw this.invalidOptions(`${path}.alg must be an integer`);
    }

    return { type, alg: algorithm };
  }

  private parseOptionalCredentialDescriptors(
    value: unknown,
    path: string,
    browserWindow: Window,
  ): PublicKeyCredentialDescriptor[] | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    return this.requireArray(value, path).map((descriptor, index) =>
      this.parseCredentialDescriptor(
        descriptor,
        `${path}[${index}]`,
        browserWindow,
      ),
    );
  }

  private parseCredentialDescriptor(
    value: unknown,
    path: string,
    browserWindow: Window,
  ): PublicKeyCredentialDescriptor {
    const descriptor = this.requireRecord(value, path);
    const parsed: PublicKeyCredentialDescriptor = {
      type: this.requirePublicKeyType(descriptor["type"], `${path}.type`),
      id: this.decodeBase64Url(
        this.requireNonEmptyString(descriptor["id"], `${path}.id`),
        `${path}.id`,
        browserWindow,
      ),
    };

    if (
      descriptor["transports"] !== undefined &&
      descriptor["transports"] !== null
    ) {
      parsed.transports = this.requireArray(
        descriptor["transports"],
        `${path}.transports`,
      ).map((transport, index) =>
        this.parseRequiredEnum(
          transport,
          `${path}.transports[${index}]`,
          AUTHENTICATOR_TRANSPORTS,
        ),
      );
    }

    return parsed;
  }

  private parseOptionalAuthenticatorSelection(
    value: unknown,
    path: string,
  ): AuthenticatorSelectionCriteria | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const selection = this.requireRecord(value, path);
    const parsed: AuthenticatorSelectionCriteria = {};
    const attachment = this.parseOptionalEnum(
      selection["authenticatorAttachment"],
      `${path}.authenticatorAttachment`,
      AUTHENTICATOR_ATTACHMENTS,
    );
    const residentKey = this.parseOptionalEnum(
      selection["residentKey"],
      `${path}.residentKey`,
      RESIDENT_KEY_REQUIREMENTS,
    );
    const userVerification = this.parseOptionalEnum(
      selection["userVerification"],
      `${path}.userVerification`,
      USER_VERIFICATION_REQUIREMENTS,
    );

    if (attachment !== undefined) {
      parsed.authenticatorAttachment = attachment;
    }
    if (residentKey !== undefined) {
      parsed.residentKey = residentKey;
    }
    if (userVerification !== undefined) {
      parsed.userVerification = userVerification;
    }
    if (selection["requireResidentKey"] !== undefined) {
      if (typeof selection["requireResidentKey"] !== "boolean") {
        throw this.invalidOptions(
          `${path}.requireResidentKey must be a boolean`,
        );
      }
      parsed.requireResidentKey = selection["requireResidentKey"];
    }

    return parsed;
  }

  private parseOptionalExtensions(
    value: unknown,
    path: string,
  ): AuthenticationExtensionsClientInputs | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    return this.requireRecord(
      value,
      path,
    ) as AuthenticationExtensionsClientInputs;
  }

  private parseOptionalStringArray(
    value: unknown,
    path: string,
  ): string[] | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    return this.requireArray(value, path).map((item, index) =>
      this.requireNonEmptyString(item, `${path}[${index}]`),
    );
  }

  private parseOptionalTimeout(
    value: unknown,
    path: string,
  ): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (
      typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > MAX_UINT32
    ) {
      throw this.invalidOptions(`${path} must be an unsigned 32-bit integer`);
    }

    return value;
  }

  private parseOptionalEnum<T extends string>(
    value: unknown,
    path: string,
    allowed: ReadonlySet<T>,
  ): T | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    return this.parseRequiredEnum(value, path, allowed);
  }

  private parseRequiredEnum<T extends string>(
    value: unknown,
    path: string,
    allowed: ReadonlySet<T>,
  ): T {
    if (typeof value !== "string" || !allowed.has(value as T)) {
      throw this.invalidOptions(`${path} contains an unsupported value`);
    }

    return value as T;
  }

  private requirePublicKeyType(value: unknown, path: string): "public-key" {
    if (value !== PUBLIC_KEY_CREDENTIAL_TYPE) {
      throw this.invalidOptions(`${path} must be "public-key"`);
    }

    return PUBLIC_KEY_CREDENTIAL_TYPE;
  }

  private serializeRegistrationCredential(
    credential: Credential | null,
    browserWindow: Window,
  ): WebAuthnRegistrationCredential {
    const publicKeyCredential = this.requirePublicKeyCredential(credential);
    const response = publicKeyCredential.response;

    if (!this.hasArrayBufferProperty(response, "attestationObject")) {
      throw new WebAuthnBrowserError(
        "operation-failed",
        "The browser returned an invalid security-key registration response.",
      );
    }

    const attestationResponse =
      response as unknown as AuthenticatorAttestationResponse;
    const transports =
      typeof attestationResponse.getTransports === "function"
        ? attestationResponse.getTransports()
        : [];

    return {
      id: publicKeyCredential.id,
      rawId: this.encodeBase64Url(publicKeyCredential.rawId, browserWindow),
      type: PUBLIC_KEY_CREDENTIAL_TYPE,
      authenticatorAttachment: publicKeyCredential.authenticatorAttachment,
      clientExtensionResults: this.serializeExtensionResults(
        publicKeyCredential.getClientExtensionResults(),
        browserWindow,
      ),
      response: {
        attestationObject: this.encodeBase64Url(
          attestationResponse.attestationObject,
          browserWindow,
        ),
        clientDataJSON: this.encodeBase64Url(
          attestationResponse.clientDataJSON,
          browserWindow,
        ),
        transports: [...transports],
      },
    };
  }

  private serializeAuthenticationCredential(
    credential: Credential | null,
    browserWindow: Window,
  ): WebAuthnAuthenticationCredential {
    const publicKeyCredential = this.requirePublicKeyCredential(credential);
    const response = publicKeyCredential.response;

    if (
      !this.hasArrayBufferProperty(response, "authenticatorData") ||
      !this.hasArrayBufferProperty(response, "signature")
    ) {
      throw new WebAuthnBrowserError(
        "operation-failed",
        "The browser returned an invalid security-key authentication response.",
      );
    }

    const assertionResponse =
      response as unknown as AuthenticatorAssertionResponse;

    return {
      id: publicKeyCredential.id,
      rawId: this.encodeBase64Url(publicKeyCredential.rawId, browserWindow),
      type: PUBLIC_KEY_CREDENTIAL_TYPE,
      authenticatorAttachment: publicKeyCredential.authenticatorAttachment,
      clientExtensionResults: this.serializeExtensionResults(
        publicKeyCredential.getClientExtensionResults(),
        browserWindow,
      ),
      response: {
        authenticatorData: this.encodeBase64Url(
          assertionResponse.authenticatorData,
          browserWindow,
        ),
        clientDataJSON: this.encodeBase64Url(
          assertionResponse.clientDataJSON,
          browserWindow,
        ),
        signature: this.encodeBase64Url(
          assertionResponse.signature,
          browserWindow,
        ),
        userHandle:
          assertionResponse.userHandle === null
            ? null
            : this.encodeBase64Url(assertionResponse.userHandle, browserWindow),
      },
    };
  }

  private requirePublicKeyCredential(
    credential: Credential | null,
  ): PublicKeyCredential {
    const candidate = credential as
      | (Credential & Partial<PublicKeyCredential>)
      | null;

    if (
      !candidate ||
      candidate.type !== PUBLIC_KEY_CREDENTIAL_TYPE ||
      !this.hasArrayBufferProperty(candidate, "rawId") ||
      !candidate.response ||
      !this.hasArrayBufferProperty(candidate.response, "clientDataJSON") ||
      typeof candidate.getClientExtensionResults !== "function"
    ) {
      throw new WebAuthnBrowserError(
        "operation-failed",
        "The browser did not return a valid security-key credential.",
      );
    }

    return candidate as PublicKeyCredential;
  }

  private serializeExtensionResults(
    value: AuthenticationExtensionsClientOutputs,
    browserWindow: Window,
  ): { [key: string]: WebAuthnJsonValue } {
    const serialized = this.serializeJsonValue(value, browserWindow);
    return this.isRecord(serialized)
      ? (serialized as { [key: string]: WebAuthnJsonValue })
      : {};
  }

  private serializeJsonValue(
    value: unknown,
    browserWindow: Window,
  ): WebAuthnJsonValue {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "boolean"
    ) {
      return value;
    }

    if (typeof value === "number") {
      if (!Number.isFinite(value)) {
        throw new WebAuthnBrowserError(
          "operation-failed",
          "The browser returned invalid security-key extension data.",
        );
      }
      return value;
    }

    if (value instanceof ArrayBuffer) {
      return this.encodeBase64Url(value, browserWindow);
    }

    if (ArrayBuffer.isView(value)) {
      const bytes = new Uint8Array(
        value.buffer,
        value.byteOffset,
        value.byteLength,
      );
      return this.encodeBase64Url(bytes.slice().buffer, browserWindow);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.serializeJsonValue(item, browserWindow));
    }

    if (this.isRecord(value)) {
      const serialized: { [key: string]: WebAuthnJsonValue } = {};
      for (const [key, nestedValue] of Object.entries(value)) {
        if (nestedValue !== undefined) {
          serialized[key] = this.serializeJsonValue(nestedValue, browserWindow);
        }
      }
      return serialized;
    }

    throw new WebAuthnBrowserError(
      "operation-failed",
      "The browser returned unsupported security-key extension data.",
    );
  }

  private decodeBase64Url(
    value: string,
    path: string,
    browserWindow: Window,
  ): ArrayBuffer {
    if (
      !BASE64URL_PATTERN.test(value) ||
      value.length % 4 === 1 ||
      (value.includes("=") && !/^[A-Za-z0-9_-]+={1,2}$/.test(value))
    ) {
      throw this.invalidOptions(`${path} must be valid base64url`);
    }

    const withoutPadding = value.replace(/=+$/, "");
    const base64 = withoutPadding.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    try {
      const binary = browserWindow.atob(padded);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }
      return bytes.buffer;
    } catch {
      throw this.invalidOptions(`${path} must be valid base64url`);
    }
  }

  private encodeBase64Url(value: ArrayBuffer, browserWindow: Window): string {
    const bytes = new Uint8Array(value);
    let binary = "";
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return browserWindow
      .btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  private hasArrayBufferProperty(value: object, property: string): boolean {
    return (
      property in value && Reflect.get(value, property) instanceof ArrayBuffer
    );
  }

  private requireRecord(value: unknown, path: string): Record<string, unknown> {
    if (!this.isRecord(value)) {
      throw this.invalidOptions(`${path} must be an object`);
    }

    return value;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  private requireArray(value: unknown, path: string): unknown[] {
    if (!Array.isArray(value)) {
      throw this.invalidOptions(`${path} must be an array`);
    }

    return value;
  }

  private requireNonEmptyString(value: unknown, path: string): string {
    if (typeof value !== "string" || value.trim().length === 0) {
      throw this.invalidOptions(`${path} must be a non-empty string`);
    }

    return value;
  }

  private invalidOptions(detail: string): WebAuthnBrowserError {
    return new WebAuthnBrowserError(
      "invalid-options",
      `The server returned invalid security-key options (${detail}). Refresh and try again.`,
    );
  }

  private mapError(
    error: unknown,
    operation: WebAuthnOperation,
  ): WebAuthnBrowserError {
    if (error instanceof WebAuthnBrowserError) {
      return error;
    }

    const errorName =
      this.isRecord(error) && typeof error["name"] === "string"
        ? error["name"]
        : null;

    switch (errorName) {
      case "NotAllowedError":
        return new WebAuthnBrowserError(
          "not-allowed",
          "No security-key response was received. Try again, or use another verification method.",
          { cause: error },
        );
      case "AbortError":
        return new WebAuthnBrowserError(
          "aborted",
          "Security-key verification was cancelled. Try again, or use another verification method.",
          { cause: error },
        );
      case "InvalidStateError":
        return new WebAuthnBrowserError(
          "invalid-state",
          operation === "registration"
            ? "This security key is already registered. Use a different key or another verification method."
            : "This security key cannot be used in its current state. Try another verification method.",
          { cause: error },
        );
      case "NotSupportedError":
        return new WebAuthnBrowserError(
          "not-supported",
          "This browser or authenticator cannot complete the security-key request. Try another key or verification method.",
          { cause: error },
        );
      case "SecurityError":
        return new WebAuthnBrowserError(
          "security",
          "Security-key verification is not allowed for this site. Check the secure address or use another verification method.",
          { cause: error },
        );
      default:
        return new WebAuthnBrowserError(
          "operation-failed",
          "The security-key request failed. Try again, or use another verification method.",
          { cause: error },
        );
    }
  }
}
